import { Toast } from '@byecode/ui'
import type { FileConfig } from '@lighthouse/core'
import { FIELD_TYPE_LIMIT_ENUM, FILE_TYPE_CATEGORY_ENUM } from '@lighthouse/core'
import { t } from 'i18next'

import { getFileSizeToMB, getFileTypeByFileName } from '../utils'
import { fileSuffixRegex } from './reg'
// 模板文件上传限制 5 M
export const exportTemplateFileSizeLimit = 5 * 1024 * 1024

//  小程序截图上传限制 2 M
export const miniProgramScreenshotsSizeLimit = 2 * 1024 * 1024

// 小程序视频上传限制 10 M
export const miniProgramVideoSizeLimit = 10 * 1024 * 1024

export const iconMaxSize = 1024 * 1024 * 5

// export const avatarMaxFileSize = 1024 * 1024 * 5

export const logoMaxUploadSize = 1024 * 1024 * 2

// 头像上传大小限制（单位：MB）
export const avatarMaxUploadSizeMB = 5
export const avatarMaxUploadSizeBytes = avatarMaxUploadSizeMB * 1024 * 1024

export const defaultFileMaxUploadSize = 1024 * 1024 * 5

export const aliyunVideoProtocolList = ['mp4']

export const audioAcceptExtensions = ['mp3', 'wma', 'm4a', 'wav', 'aac', 'ogg', 'opus', 'flac', 'rm', 'mpg']

/**
 * FieldInput(file) 的固定限制规则（与 `@lighthouse/core` 的 FileConfig 结构一致，单位：MB）
 */
export const AVATAR_FILE_INPUT_LIMITS = {
    uploadLengthMinLimit: 0,
    uploadLengthMaxLimit: 1,
    fileLimitType: FIELD_TYPE_LIMIT_ENUM.TYPE,
    fileType: FILE_TYPE_CATEGORY_ENUM.IMAGE
}

export const DEFAULT_FILE_INPUT_LIMITS = {
    fileLimitType: FIELD_TYPE_LIMIT_ENUM.ALL,
    fileSizeLimit: 5
}

/**
 * 视频字段固定限制规则（单位：MB）
 * - 目前仅允许 mp4
 */
export const VIDEO_FILE_INPUT_LIMITS = {
    fileLimitType: FIELD_TYPE_LIMIT_ENUM.SUFFIX,
    fileSuffix: 'mp4'
}

/**
 * FieldInput(file) 的固有规则（运行时兜底）
 * - AVATAR：数量/类型走固定规则，单文件大小用用户配置
 * - video：强制后缀 mp4（不覆盖数量/大小等其他配置）
 */
export function getEffectiveFileInputConfig(params: {
    fieldId?: string
    fieldType?: string
    config?: Partial<FileConfig['file']>
}): Partial<FileConfig['file']> | undefined {
    const { fieldId, fieldType, config } = params
    const normalizedFieldId = (fieldId ?? '').toUpperCase()
    if (normalizedFieldId === 'AVATAR') {
        return {
            ...AVATAR_FILE_INPUT_LIMITS,
            fileSizeLimit: config?.fileSizeLimit ?? DEFAULT_FILE_INPUT_LIMITS.fileSizeLimit
        }
    }
    if (fieldType === 'video') {
        return {
            ...(config ?? {}),
            ...VIDEO_FILE_INPUT_LIMITS,
            // 避免 TYPE 与 SUFFIX 同时残留
            fileType: undefined
        }
    }
    return config
}

function getNormalizedFileLimitType(value: FIELD_TYPE_LIMIT_ENUM | undefined): FIELD_TYPE_LIMIT_ENUM {
    return value ?? DEFAULT_FILE_INPUT_LIMITS.fileLimitType
}

function getNormalizedFileSizeLimit(value: number | undefined): number {
    return value === undefined ? DEFAULT_FILE_INPUT_LIMITS.fileSizeLimit : value
}

function parseSuffixList(fileSuffix?: string): string[] {
    if (!fileSuffix) {
        return []
    }
    return fileSuffix
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
        .map(s => (s.startsWith('.') ? s.slice(1) : s))
}

export function getAcceptByFileConfig(config?: Partial<FileConfig['file']>, uploadFileType?: 'file' | 'video'): string | undefined {
    const limitType = getNormalizedFileLimitType(config?.fileLimitType)

    if (limitType === FIELD_TYPE_LIMIT_ENUM.TYPE) {
        switch (config?.fileType) {
            case FILE_TYPE_CATEGORY_ENUM.IMAGE: {
                return 'image/*'
            }
            case FILE_TYPE_CATEGORY_ENUM.VIDEO: {
                return 'video/*'
            }
            case FILE_TYPE_CATEGORY_ENUM.AUDIO: {
                return 'audio/*'
            }
            default: {
                return undefined
            }
        }
    }

    if (limitType === FIELD_TYPE_LIMIT_ENUM.SUFFIX) {
        const list = parseSuffixList(config?.fileSuffix)
        if (list.length === 0) {
            return undefined
        }
        return list.map(ext => `.${ext}`).join(',')
    }

    // ALL：不设置 accept，沿用组件内部默认（video 默认 video/mp4）
    return uploadFileType === 'video' ? 'video/mp4' : undefined
}

export function getUploadLengthLimitByFileConfig(config?: Partial<FileConfig['file']>): {
    min: number | undefined
    max: number | undefined
} {
    return {
        min: Number.isFinite(config?.uploadLengthMinLimit) ? Number(config?.uploadLengthMinLimit) : undefined,
        max: Number.isFinite(config?.uploadLengthMaxLimit) ? Number(config?.uploadLengthMaxLimit) : undefined
    }
}

function isAllowedByCategory(file: File, category: FILE_TYPE_CATEGORY_ENUM): boolean {
    const fileType = getFileTypeByFileName(file.name)
    switch (category) {
        case FILE_TYPE_CATEGORY_ENUM.IMAGE: {
            return fileType === 'image'
        }
        case FILE_TYPE_CATEGORY_ENUM.VIDEO: {
            return fileType === 'video'
        }
        case FILE_TYPE_CATEGORY_ENUM.AUDIO: {
            return fileType === 'audio'
        }
        case FILE_TYPE_CATEGORY_ENUM.DOCUMENT: {
            return !['image', 'video', 'audio'].includes(fileType)
        }
        default: {
            return true
        }
    }
}

function isAllowedBySuffix(file: File, allowedSuffixList: string[]): boolean {
    if (allowedSuffixList.length === 0) {
        return true
    }
    const extension = fileSuffixRegex.exec(file.name.toLocaleLowerCase())?.[1]?.toLocaleLowerCase()
    if (!extension) {
        return false
    }
    return allowedSuffixList.includes(extension)
}

/**
 * 生成 FieldInput(file) 的上传过滤器：
 * - 单文件大小：有 config.fileSizeLimit(MB) 则用该限制，否则用 DEFAULT_FILE_INPUT_LIMITS.fileSizeLimit，未定义则不限制
 * - 支持 fileLimitType: ALL/TYPE/SUFFIX
 */
export function createFieldInputFileFilter(params: {
    fieldType?: string
    fieldId?: string
    config?: Partial<FileConfig['file']>
}): (file: File | string, index: number) => boolean {
    const { fieldId, fieldType } = params
    const config = getEffectiveFileInputConfig({ fieldId, fieldType, config: params.config })
    const isAvatarField = (fieldId ?? '').toUpperCase() === 'AVATAR'

    const limitType = getNormalizedFileLimitType(config?.fileLimitType)
    const allowedSuffixList = limitType === FIELD_TYPE_LIMIT_ENUM.SUFFIX ? parseSuffixList(config?.fileSuffix) : []

    const fileSizeLimitMB = getNormalizedFileSizeLimit(config?.fileSizeLimit)
    const effectiveMaxBytes = fileSizeLimitMB * 1024 * 1024

    return (file: File | string) => {
        if (!(file instanceof File)) {
            return true
        }

        if (effectiveMaxBytes !== undefined && file.size > effectiveMaxBytes) {
            Toast.error(t('fileExceedsUploadLimit', { v: getFileSizeToMB(effectiveMaxBytes) }))
            return false
        }

        if (limitType === FIELD_TYPE_LIMIT_ENUM.TYPE && config?.fileType) {
            if (!isAllowedByCategory(file, config.fileType)) {
                if (isAvatarField && config.fileType === FILE_TYPE_CATEGORY_ENUM.IMAGE) {
                    Toast.error(t('onlyPicture'))
                } else {
                    Toast.error(t('fileTypeNotSupported'))
                }
                return false
            }
        }

        if (limitType === FIELD_TYPE_LIMIT_ENUM.SUFFIX) {
            if (!isAllowedBySuffix(file, allowedSuffixList)) {
                if (fieldType === 'video') {
                    Toast.error(t('unsupportedVideoFormatPleaseUploadMP4file'))
                } else {
                    Toast.error(t('fileTypeNotSupported'))
                }
                return false
            }
        }

        return true
    }
}

export const fileSizeFilter = (maxUploadSize: number, filter?: (file: File) => boolean) => {
    return (file: string | File, index: number) => {
        if (file instanceof File) {
            if (file.size > maxUploadSize) {
                Toast.error(t('fileExceedsUploadLimit', { v: getFileSizeToMB(maxUploadSize) }))
                return false
            }
            if (filter && !filter(file)) {
                return false
            }
        }
        return true
    }
}
export const videoFileSizeFilter = (maxUploadSize: number) => {
    return (file: File | string, index: number) => {
        if (file instanceof File) {
            if (file.size > maxUploadSize) {
                Toast.error(t('fileExceedsUploadLimit', { v: getFileSizeToMB(maxUploadSize) }))
                return false
            }
            const extension = fileSuffixRegex.exec(file.name.toLocaleLowerCase())?.[1]?.toLocaleLowerCase()
            if (!extension || !aliyunVideoProtocolList.includes(extension)) {
                Toast.error(t('unsupportedVideoFormatPleaseUploadMP4file'))
                return false
            }
            return true
        }
        return true
    }
}

export const audioFileSizeFilter = (maxUploadSize: number) => {
    return (file: File | string, index: number) => {
        if (file instanceof File) {
            if (file.size > maxUploadSize) {
                Toast.error(t('fileExceedsUploadLimit', { v: getFileSizeToMB(maxUploadSize) }))
                return false
            }
            const extension = fileSuffixRegex.exec(file.name.toLocaleLowerCase())?.[1]?.toLocaleLowerCase()
            if (!extension || !audioAcceptExtensions.includes(extension)) {
                Toast.error(t('fileTypeNotSupported'))
                return false
            }
            return true
        }
        return true
    }
}
